import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { requestProducts } from 'src/redux/products';
import { Filter, SelectedFilterOption } from 'src/types';
import {
  getDefaultSelectedFilterValues,
  isSameFilterOption,
  updateSelectedFilterOptions,
} from './helpers/reducerHelpers';
import {
  getFiltersWithSortFilter,
  getSelectedFilterOptionsWithSortFilter,
} from './helpers/reducerHelpers/getSelectedFilterOptions';
import {
  FilterOptionSelection,
  getFilterOptionSelection,
} from './selectors/selectFilterOptionSelection';

export interface FilterState {
  selectedFilterOptions: SelectedFilterOption[];
  filters: Filter[];
  lastUpdatedFilter: FilterOptionSelection | null;
}

export const initialState: Readonly<FilterState> = {
  selectedFilterOptions: [],
  filters: [],
  lastUpdatedFilter: null,
};

export const { actions, reducer } = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    selectFilterOption: (state, action: PayloadAction<SelectedFilterOption>) => {
      const newFilterOption = action.payload;

      state.lastUpdatedFilter = getFilterOptionSelection(
        newFilterOption,
        state.filters,
        state.selectedFilterOptions,
      );

      const containsFilterOption = Boolean(
        state.selectedFilterOptions.find((filter) => isSameFilterOption(filter, newFilterOption)),
      );

      if (containsFilterOption) {
        state.selectedFilterOptions = updateSelectedFilterOptions(
          state.selectedFilterOptions,
          newFilterOption,
        );
      } else {
        state.selectedFilterOptions = state.selectedFilterOptions.concat([newFilterOption]);
      }
    },
    resetFilters: (state) => {
      state.selectedFilterOptions = getDefaultSelectedFilterValues(state.filters);
    },
    resetFilterOptions: (state, action: PayloadAction<Filter>) => {
      const filter = R.find(R.propEq('uid', action.payload.uid), state.filters);
      const defaultSelectedFilterValueWrapped = filter
        ? getDefaultSelectedFilterValues([filter])
        : [];
      const defaultSelectedFilterValue =
        defaultSelectedFilterValueWrapped.length > 0
          ? defaultSelectedFilterValueWrapped[0]
          : undefined;

      if (defaultSelectedFilterValue) {
        state.selectedFilterOptions = state.selectedFilterOptions.map((selectedFilterOption) =>
          selectedFilterOption.filterUid === action.payload.uid
            ? defaultSelectedFilterValue
            : selectedFilterOption,
        );
      } else {
        state.selectedFilterOptions = state.selectedFilterOptions.filter(
          (selectedFilterOption) => selectedFilterOption.filterUid !== action.payload.uid,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(requestProducts.fulfilled, (state, { payload }) => {
      if (payload.data?.filters) {
        state.selectedFilterOptions = getSelectedFilterOptionsWithSortFilter(
          payload.data.filters,
          payload.data.sortingOptions,
        );
        state.filters = getFiltersWithSortFilter(payload.data.filters, payload.data.sortingOptions);
      }
    });
  },
});
